// ── Objetos de credenciales que no se pueden imprimir (2026-08-25) ───────────
// Incidente real: `getImapCreds()` devolvía un objeto plano con la contraseña
// en claro. Un error de programación lo pasó como argumento a un `Error`, el
// error se volcó, y la contraseña IMAP de un cliente terminó impresa completa
// en un log.
//
// Nadie escribió "imprimí la contraseña". Ese es el punto: el objeto era
// imprimible por defecto, así que cualquier camino que lo serializara —un
// `console.log` de depuración, un `JSON.stringify`, una propiedad de un Error,
// el volcado de una excepción no atrapada— la exponía. La defensa no puede ser
// acordarse en cada uno de esos caminos.
//
// Acá se invierte: el objeto se marca para que TODA vía de serialización
// devuelva la contraseña censurada, mientras la propiedad sigue siendo un
// string usable por la librería que la necesita.
//
// LÍMITE CONOCIDO, a propósito: copiar el objeto pierde la protección —
// `{ ...creds }` o `{ user: creds.user, pass: creds.pass }` producen un objeto
// nuevo y desnudo. Es inevitable sin envolver el valor en algo que no sea un
// string (y ahí se rompe el llamador). Por eso esto es UNA capa: la otra es la
// censura por nombre de campo en el logger, que sí alcanza a esas copias.
/** Nombres de campo que nunca deben imprimirse. Compartido con la censura del
 *  logger para que las dos capas cubran exactamente lo mismo. */
export const SECRET_FIELD_NAMES = [
    'pass', 'password', 'passwd',
    'secret', 'clientSecret', 'client_secret',
    'token', 'accessToken', 'access_token', 'refreshToken', 'refresh_token',
    'apiKey', 'api_key', 'apiSecret',
    'authorization', 'auth',
];
export const SECRET_PLACEHOLDER = '[REDACTADO]';
const SECRET_SET = new Set(SECRET_FIELD_NAMES.map(n => n.toLowerCase()));
function isSecretField(key) {
    return SECRET_SET.has(key.toLowerCase());
}
/** Copia con los campos sensibles censurados, recursiva. */
export function censorSecretFields(value, depth = 0) {
    if (depth > 6 || value == null)
        return value;
    if (Array.isArray(value))
        return value.map(v => censorSecretFields(v, depth + 1));
    if (typeof value !== 'object')
        return value;
    const out = {};
    for (const [k, v] of Object.entries(value)) {
        out[k] = isSecretField(k) && v != null ? SECRET_PLACEHOLDER : censorSecretFields(v, depth + 1);
    }
    return out;
}
/**
 * Rutas para el `redact` de pino — la segunda capa.
 *
 * Cubre cada nombre sensible en la raíz y hasta tres niveles de anidamiento con
 * comodín, más los lugares donde un objeto suele viajar dentro de un error
 * (`err.*`) o de una petición HTTP. pino no soporta un comodín de profundidad
 * arbitraria, así que la lista es explícita a propósito: prefiero que se vea
 * hasta dónde llega y no creer que cubre todo.
 */
export function buildRedactPaths() {
    const prefijos = ['', '*.', '*.*.', '*.*.*.', 'err.', 'err.*.', 'req.body.', 'req.headers.'];
    const rutas = [];
    for (const p of prefijos) {
        for (const campo of SECRET_FIELD_NAMES)
            rutas.push(`${p}${campo}`);
    }
    // La cabecera de autorización viaja con este nombre exacto.
    rutas.push('req.headers["authorization"]', 'req.headers.cookie', 'headers.cookie');
    return rutas;
}
// Símbolo con el que Node decide cómo imprimir un objeto (console.log,
// util.inspect, el volcado de una excepción no atrapada). Se obtiene por
// Symbol.for y NO importando 'node:util': este paquete también lo consume el
// frontend, donde ese import rompe el build.
const NODE_INSPECT = Symbol.for('nodejs.util.inspect.custom');
/**
 * Marca un objeto de credenciales para que no se pueda imprimir en claro.
 *
 * Devuelve el MISMO objeto (no una copia): `creds.pass` sigue siendo el string
 * real y quien lo necesite lo usa igual. Lo que cambia es cómo se serializa.
 *
 * Cubre las tres vías por las que se filtró y se puede filtrar:
 *   - `JSON.stringify(creds)`         → toJSON
 *   - `console.log(creds)` / inspect  → nodejs.util.inspect.custom
 *   - pino (`logger.info({ creds })`) → honra toJSON
 *
 * Los marcadores van como propiedades NO enumerables: no aparecen en
 * `Object.keys`, no se copian con spread, y no llegan como opción extra a la
 * librería que recibe el objeto.
 */
export function protectSecrets(creds) {
    if (Object.prototype.hasOwnProperty.call(creds, 'toJSON'))
        return creds;
    const censurado = () => censorSecretFields(creds);
    Object.defineProperty(creds, 'toJSON', {
        value: censurado, enumerable: false, configurable: true, writable: true,
    });
    Object.defineProperty(creds, NODE_INSPECT, {
        value: censurado, enumerable: false, configurable: true, writable: true,
    });
    return creds;
}
//# sourceMappingURL=protectSecrets.js.map